import ApplicationController from 'javascript/controllers/application_controller';
import * as d3 from 'd3';

function getContainerWidth(container) {
  return container.node() ? container.node().getBoundingClientRect().width : 0;
}

function getGraphHeight(container) {
  const containerWidth = getContainerWidth(container);

  return containerWidth >= 640 ? 330 : 260;
}

export default class extends ApplicationController {
  static targets = [
    'statsPath',
    'graphContainer',
    'graphElement',
    'openPercentageRate',
    'clickPercentageRate',
    'unqiueOpens',
    'unqiueClicks',
    'opensReportCard',
    'clicksReportCard',
  ];

  get statsPath() {
    return this.statsPathTarget.getAttribute('data-stats-path');
  }

  connect() {
    this.redrawChart = this.redrawChart.bind(this);

    this.sendStatsGetRequest(this.statsPath).then((data) => {
      this.buildChart(data);
      this.setReportCardData(data.report_clicks, data.report_opens);
    });

    this.graphContainerWidth = this.graphElementTarget.offsetWidth;
  }

  disconnect() {
    window.removeEventListener('resize', this.redrawChart);
  }

  redrawChart() {
    if (!this.container) return;

    const width = getContainerWidth(this.container);
    const height = getGraphHeight(this.container);

    this.renderChart(this.dataset, width, height);
  }

  sendStatsGetRequest(url) {
    this.data.set('loading', 'true');

    return window.fetch(url).then((r) => r.json());
  }

  buildChart(data) {
    this.container = d3.select('.js-line-chart-container');

    this.dataset = data.graph_data;
    this.renderChart(
      this.dataset,
      this.graphContainerWidth,
      getGraphHeight(this.container),
    );

    window.addEventListener('resize', this.redrawChart);
  }

  renderChart(data, width, height) {
    this.container.selectAll('*').remove();

    const margin = { top: 20, right: 20, bottom: 30, left: 40 };

    const svg = this.container
      .append('svg')
      .attr('width', width)
      .attr('height', height);

    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const parseDate = d3.timeParse('%Y-%m-%d');

    // flatten data if needed (britecharts-style structure assumed)
    const values = data.map((d) => ({
      date: parseDate(d.fullDate),
      value: d.value,
    }));

    const x = d3
      .scaleTime()
      .domain(d3.extent(values, (d) => d.date))
      .range([0, innerWidth]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(values, (d) => d.value)])
      .nice()
      .range([innerHeight, 0]);

    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.value))
      .curve(d3.curveBasis);

    // X axis
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(d3.axisBottom(x));

    // Y axis
    g.append('g').call(d3.axisLeft(y));

    // Line path
    g.append('path')
      .datum(values)
      .attr('fill', 'none')
      .attr('stroke', '#3b82f6')
      .attr('stroke-width', 2)
      .attr('d', line);

    // dots
    g.selectAll('circle')
      .data(values)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.value))
      .attr('r', 3)
      .attr('fill', '#3b82f6');
  }

  setReportCardData(clickStatsData, openStatsData) {
    this.openPercentageRateTarget.innerHTML = openStatsData.open_rate;
    this.unqiueOpensTarget.innerHTML = openStatsData.unique_opens;

    this.clickPercentageRateTarget.innerHTML = clickStatsData.click_rate;
    this.unqiueClicksTarget.innerHTML = clickStatsData.unique_clicks;

    this.setSimpleReportCardContentColor(openStatsData.open_rate, 'opens');
    this.setSimpleReportCardContentColor(
      openStatsData.open_rate,
      'clicks',
      6,
      3,
    );

    this.data.set('loading', 'false');
  }

  setSimpleReportCardContentColor(
    statRate,
    reportCardType,
    successPercentage = 25,
    warningPercentage = 15,
  ) {
    let target = null;

    if (reportCardType === 'opens') {
      target = this.opensReportCardTarget;
    } else if (reportCardType === 'clicks') {
      target = this.clicksReportCardTarget;
    }

    if (!target) return;

    if (statRate >= successPercentage) {
      target.classList.add('campaign-report-card-simple__content--success');
    } else if (statRate < warningPercentage) {
      target.classList.add('campaign-report-card-simple__content--warning');
    }
  }
}
